import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { User } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { useAuthStore } from '../../store/authStore';

interface CoverageStripProps {
  /** Linked agents are only shown to the property's own claimer. */
  isOwnedByUser: boolean;
}

/**
 * One quiet line under AddressHeader, owner only: the agent(s) linked to
 * this account, e.g. "Your agent: Jamie Rivera, California FAIR Plan".
 * Renders nothing for visitors or when no agent is linked. The preparation
 * model is NOT repeated here: the checklist section right below names it
 * in its own header, and a bare model name under the address read as an
 * unexplained chip (Jason, Sept 3 2026).
 */
export function CoverageStrip({ isOwnedByUser }: CoverageStripProps) {
  const { colors } = useTheme();
  const linkedAgents = useAuthStore((s) => s.user?.linkedAgents);

  const agents = isOwnedByUser ? (linkedAgents ?? []) : [];
  if (agents.length === 0) return null;

  return (
    <View style={styles.list} testID="structure-coverage-strip">
      {agents.map((agent) => (
        <View key={agent.id} style={styles.row} testID="structure-agent-row">
          <User size={14} color={colors.textSecondary} />
          <Text style={[styles.text, { color: colors.textSecondary }]} numberOfLines={1}>
            Your agent: {agent.name}, {agent.insurerName}
          </Text>
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  text: {
    ...TYPOGRAPHY.bodySmall,
  },
});
